import { memo, useCallback, useEffect, useState } from 'react';
import { AlertBanner, Button, Input, Switch } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Pencil, TriangleAlert } from 'lucide-react';

import { BLOCKING_ISSUES, ISSUE, ROW_STATUS } from '../../../constants/reviewFilters.js';
import { fileUserType, isOwnerWord } from '../../../utils/accessRules.js';
import ImportDialog from '../../shared/ImportDialog.jsx';

const toList = (text) =>
  text
    .split(/[;,|/]/)
    .map((item) => item.trim())
    .filter(Boolean);

/**
 * Fix / Edit dialog of one row: person and profile fields, what the file says about access,
 * the roles and groups it asks for and an Assign access now switch. The issues banner shows what
 * the row had when it was last checked and is re-checked only on Save, never while typing: Save
 * validates the row; the dialog closes once it is clean and stays open, with the new banner, if
 * it is not. Remove from list takes the row out of the batch. Mount it only while open.
 *
 * @param {object} props
 * @param {object} props.row The live row of the wizard state.
 * @param {(changes: object) => void} props.onSave Dispatches `FIX_ROW`.
 * @param {(rowNumber: number) => void} props.onRemove Takes the row off the list and closes.
 * @param {() => void} props.onClose
 */
function FixRowDialog({ row, onSave, onRemove, onClose }) {
  const { t } = useTranslation('people-access');
  const [person, setPerson] = useState(row.person);
  const [roles, setRoles] = useState(row.accessFromFile.roles.join(', '));
  const [groups, setGroups] = useState(row.accessFromFile.groups.join(', '));
  const [fileType, setFileType] = useState(row.accessFromFile.user_type);
  const [assignNow, setAssignNow] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (saved && row.status !== ROW_STATUS.ERROR) onClose();
  }, [saved, row.status, onClose]);

  const handleSave = useCallback(() => {
    onSave({
      rowNumber: row.rowNumber,
      person: {
        ...person,
        email: person.email.trim().toLowerCase(),
        reports_to: person.reports_to.trim().toLowerCase(),
      },
      accessFromFile: { ...row.accessFromFile, user_type: fileType, roles: toList(roles), groups: toList(groups) },
      assignNow,
    });
    setSaved(true);
  }, [onSave, row.rowNumber, row.accessFromFile, fileType, person, roles, groups, assignNow]);
  const handleRemove = useCallback(() => onRemove(row.rowNumber), [onRemove, row.rowNumber]);
  const handleImportAsMember = useCallback(() => setFileType('member'), []);
  const handleApplyFromFile = useCallback(() => setAssignNow(true), []);

  const blocking = row.issues.filter((issue) => BLOCKING_ISSUES.includes(issue.code));
  // What the file lists, following the roles and groups being edited below.
  const listedRoles = toList(roles);
  const listedGroups = toList(groups);
  // Owner is not a Member. fileUserType folds that word into 'member', which would hide it here.
  const listedType = isOwnerWord(fileType)
    ? t('terms.userType.owner')
    : t(`terms.userType.${fileUserType(fileType)}`);
  const fromFile = [listedType, ...listedRoles, ...listedGroups].join(', ');
  // The lists the file wrote are edited here only when one of its names is not in the catalog.
  const hasListIssues = blocking.some((issue) => [ISSUE.UNKNOWN_ROLE, ISSUE.UNKNOWN_GROUP].includes(issue.code));
  const hasFileAccess =
    listedRoles.length + listedGroups.length > 0 || fileUserType(fileType) === 'admin' || isOwnerWord(fileType);
  const field = (key, label) => (
    // The prototype shows the values with no label on top: the name stays for screen readers.
    <Input
      aria-label={t(`import.review.fix.${label}`)}
      placeholder={t(`import.review.fix.${label}`)}
      value={person[key]}
      onChange={(event) => setPerson((p) => ({ ...p, [key]: event.target.value }))}
    />
  );

  return (
    <ImportDialog
      onClose={onClose}
      titleId="fix-row-title"
      icon={
        blocking.length > 0 ? (
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
            <TriangleAlert size={20} aria-hidden />
          </span>
        ) : (
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
            <Pencil size={20} aria-hidden />
          </span>
        )
      }
      title={t(blocking.length > 0 ? 'import.review.fix.title' : 'import.review.fix.editTitle', {
        row: row.rowNumber,
      })}
      subtitle={t(blocking.length > 0 ? 'import.review.fix.subtitle' : 'import.review.fix.editSubtitle')}
      footer={
        <>
          <Button variant="ghost" customButtonHeight="!h-[40px]" className="!px-6" onClick={handleRemove}>
            {t('import.review.fix.removeFromList')}
          </Button>
          <Button variant="secondary" customButtonHeight="!h-[40px]" className="!px-6" data-autofocus onClick={onClose}>
            {t('import.cancel')}
          </Button>
          <Button variant="primary" customButtonHeight="!h-[40px]" className="!px-6" onClick={handleSave}>
            {t('import.review.fix.save')}
          </Button>
        </>
      }
    >
      <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto py-2 pr-1 scrollbar-aimsos-auto">
        {blocking.length > 0 ? (
          <AlertBanner
            tone="danger"
            title={blocking
              .map((issue) => t(`import.review.fix.issueMessages.${issue.code}`, { value: issue.value }))
              .join(' ')}
          />
        ) : null}
        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="mb-1 text-xs font-semibold text-content-secondary">{t('import.review.fix.person')}</legend>
          {field('email', 'email')}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field('first_name', 'firstName')}
            {field('last_name', 'lastName')}
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="mb-1 text-xs font-semibold text-content-secondary">{t('import.review.fix.profile')}</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field('job_title', 'jobTitle')}
            {field('location', 'location')}
            {field('reports_to', 'reportsTo')}
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="mb-1 text-xs font-semibold text-content-secondary">{t('import.review.fix.accessFile')}</legend>
          <Switch
            checked={assignNow}
            onChange={setAssignNow}
            label={t('import.review.fix.assignNow')}
            description={t('import.review.fix.assignNowHint')}
          />
          <div className="flex items-center justify-between gap-3">
            <p className="m-0 text-xs text-content-secondary">
              {hasFileAccess ? t('import.review.fix.yourFileLists', { access: fromFile }) : t('import.review.fix.yourFileListsNothing')}
            </p>
            <Button variant="secondary" size="sm" disabled={assignNow} onClick={handleApplyFromFile}>
              {t('import.review.fix.applyFromFile')}
            </Button>
          </div>
          {isOwnerWord(row.accessFromFile.user_type) ? (
            <div className="flex items-center justify-between gap-3">
              <p className="m-0 text-xs text-content-secondary">
                {isOwnerWord(fileType) ? t('import.review.fix.ownerInFile') : t('import.review.fix.ownerChanged')}
              </p>
              <Button variant="secondary" size="sm" disabled={!isOwnerWord(fileType)} onClick={handleImportAsMember}>
                {t('import.review.fix.importAsMember')}
              </Button>
            </div>
          ) : null}
          {hasListIssues ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label={t('import.review.fix.roles')}
                helperText={t('import.review.fix.listHint')}
                value={roles}
                onChange={(event) => setRoles(event.target.value)}
              />
              <Input
                label={t('import.review.fix.groups')}
                value={groups}
                onChange={(event) => setGroups(event.target.value)}
              />
            </div>
          ) : null}
        </fieldset>
      </div>
    </ImportDialog>
  );
}

export default memo(FixRowDialog);
